import Enumerable from "linq";
import { Point, Size, PaintStyle, TextureKinds } from "../../../../../domain/drawing";

export interface ITextureCreatable {
  create(style: PaintStyle, offset: Point): HTMLCanvasElement;
}

export class TextureFactory {
  create(style: PaintStyle, offset: Point): HTMLCanvasElement[] {
    return Enumerable.from([
      this._create(style, style.kind, offset),
      this._create(style, style.additionalTextureKind, offset),
    ])
    .where(e => !!e)
    .toArray();
  }

  private _create(style: PaintStyle, kind: TextureKinds, offset: Point): HTMLCanvasElement {
    const factory = Enumerable.from(this._factories)
      .firstOrDefault(f => f.kind === kind);
    return factory
      ? factory.instance.create(style, offset)
      : null;
  }

  private readonly _factories: { kind: TextureKinds, instance: ITextureCreatable }[] = [
    {
      kind: TextureKinds.GrayDiagonalCross,
      instance: new StaticTextureFactory(new Size(8, 8), (c, s, o) => {
        c.strokeStyle = "gray";
        c.lineWidth = 1;
        TextureDrawer.strokeLineFromTopLeft(c, s, o);
        TextureDrawer.strokeLineFromTopRight(c, s, o);
      }),
    },
    {
      kind: TextureKinds.Fill,
      instance: new DynamicTextureFactory(new Size(1, 1), TextureDrawer.fill),
    },
    {
      kind: TextureKinds.LinesFromTopLeft,
      instance: new DynamicTextureFactory(new Size(8, 8), TextureDrawer.strokeLineFromTopLeft),
    },
    {
      kind: TextureKinds.LinesFromTopRight,
      instance: new DynamicTextureFactory(new Size(8, 8), TextureDrawer.strokeLineFromTopRight),
    },
    {
      kind: TextureKinds.SmallDot,
      instance: new DynamicTextureFactory(new Size(8, 8), (c, s, o) => {
        TextureDrawer.fillCircle(c, 4 + o.x, 4 + o.y, 1.5);
        TextureDrawer.fillCircle(c, -s.width + 4 + o.x, 4 + o.y, 1.5);
        TextureDrawer.fillCircle(c, 4 + o.x, -s.height + 4 + o.y, 1.5);
        TextureDrawer.fillCircle(c, -s.width + 4 + o.x, -s.height + 4 + o.y, 1.5);
      }),
    },
    {
      kind: TextureKinds.LargeDot,
      instance: new DynamicTextureFactory(new Size(12, 12), (c, s, o) => {
        TextureDrawer.fillCircle(c, 6 + o.x, 6 + o.y, 3.5);
        TextureDrawer.fillCircle(c, -s.width + 6 + o.x, 6 + o.y, 3.5);
        TextureDrawer.fillCircle(c, 6 + o.x, -s.height + 6 + o.y, 3.5);
        TextureDrawer.fillCircle(c, -s.width + 6 + o.x, -s.height + 6 + o.y, 3.5);
      }),
    },
    {
      kind: TextureKinds.LinesFromTop,
      instance: new DynamicTextureFactory(new Size(8, 8), TextureDrawer.strokeLineFromTop),
    },
    {
      kind: TextureKinds.LinesFromLeft,
      instance: new DynamicTextureFactory(new Size(8, 8), TextureDrawer.strokeLineFromLeft),
    },
    {
      kind: TextureKinds.DiagonalCross,
      instance: new DynamicTextureFactory(new Size(8, 8), (c, s, o) => {
        TextureDrawer.strokeLineFromTopLeft(c, s, o);
        TextureDrawer.strokeLineFromTopRight(c, s, o);
      }),
    },
    {
      kind: TextureKinds.Cross,
      instance: new DynamicTextureFactory(new Size(8, 8), (c, s, o) => {
        TextureDrawer.strokeLineFromTop(c, s, o);
        TextureDrawer.strokeLineFromLeft(c, s, o);
      }),
    },
  ];
}

abstract class AbstractTextureFactory implements ITextureCreatable {
  constructor(private readonly _size: Size) {}

  create(style: PaintStyle, offset: Point): HTMLCanvasElement {
    const canvas = this.createCanvas();
    this.draw(canvas.getContext("2d"), this._size, style, offset);
    return canvas;
  }

  private createCanvas(): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.width = this._size.width;
    canvas.height = this._size.height;
    return canvas;
  }

  protected createContext(canvas: HTMLCanvasElement, width: number, height: number): CanvasRenderingContext2D {
    canvas.width = width;
    canvas.height = height;
    return canvas.getContext("2d");
  }

  protected abstract draw(context: CanvasRenderingContext2D, size: Size, style: PaintStyle, offset: Point): void;
}

class DynamicTextureFactory extends AbstractTextureFactory {
  constructor(size: Size, private readonly _draw: (context: CanvasRenderingContext2D, size: Size, offset: Point) => void) {
    super(size);
  }

  protected draw(context: CanvasRenderingContext2D, size: Size, style: PaintStyle, offset: Point): void {
    context.strokeStyle = style.color.value;
    context.lineWidth = 1;
    context.fillStyle = style.color.value;
    this._draw(context, size, offset);
  }
}

class TextureDrawer {
  static strokeLineFromTopLeft(context: CanvasRenderingContext2D, size: Size, offset: Point): void {
    for (let y = offset.y - offset.x - size.height; y < size.height * 2; y += size.height) {
      TextureDrawer.strokeLine(context, 0, y, size.width, y + size.height);
    }
  }

  static strokeLineFromTopRight(context: CanvasRenderingContext2D, size: Size, offset: Point): void {
    for (let y = offset.y + offset.x - size.height; y < size.height * 2; y += size.height) {
      TextureDrawer.strokeLine(context, size.width, y, 0, y + size.height);
    }
  }

  static strokeLineFromTop(context: CanvasRenderingContext2D, size: Size, offset: Point): void {
    for (let x = offset.x - size.width - 1; x < size.width * 2; x += size.width) {
      TextureDrawer.strokeLine(context, x, 0, x, size.height);
    }
  }

  static strokeLineFromLeft(context: CanvasRenderingContext2D, size: Size, offset: Point): void {
    for (let y = offset.y - size.height - 1; y < size.height * 2; y += size.height) {
      TextureDrawer.strokeLine(context, 0, y, size.width, y);
    }
  }

  static strokeLine(context: CanvasRenderingContext2D, fromX: number, fromY: number, toX: number, toY: number): void {
    context.beginPath();
    context.moveTo(fromX, fromY);
    context.lineTo(toX, toY);
    context.stroke();
  }

  static fill(context: CanvasRenderingContext2D, size: Size): void {
    context.rect(0, 0, size.width, size.height);
    context.fill();
  }

  static fillCircle(context: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number): void {
    context.beginPath();
    context.arc(centerX, centerY, radius, this.fromAngle(0), this.fromAngle(360), false);
    context.fill();
    context.stroke();
  }

  static fromAngle(angle: number): number {
    return angle * Math.PI / 180;
  }
}

class StaticTextureFactory extends AbstractTextureFactory {
  constructor(size: Size, private readonly _draw: (context: CanvasRenderingContext2D, size: Size, offset: Point) => void) {
    super(size);
  }

  protected draw(context: CanvasRenderingContext2D, size: Size, style: PaintStyle, offset: Point): void {
    this._draw(context, size, offset);
  }
}
